const HI_PHOTO_EDIT_CANVAS_ID = 'hi-photo-edit-canvas';
const MAX_SIZE = 1536;

const degToRad = (degrees) => degrees * Math.PI / 180

export class PhotoEditService {

  getCanvas(): HTMLCanvasElement {
    return document.getElementById(HI_PHOTO_EDIT_CANVAS_ID) as HTMLCanvasElement;
  }

  scaleValue(origValue: number, changedValue: number, valueToScale: number) {
    const scalingFactor = origValue / changedValue;
    return Math.floor(valueToScale / scalingFactor);
  }

  /**
   * Restricts the width and height values stored in `rect` to be no greater
   * than MAX_SIZE, and scales down the width and/or height while preserving
   * aspect ratio.
   * 
   * @param {*} rect 
   */
  restrictSize(rect: { width: number, height: number }, sizeLimit?: number) {
    let width, height;

    sizeLimit = sizeLimit || MAX_SIZE;

    width = rect.width;
    height = rect.height;

    if (width > MAX_SIZE) {
      height = this.scaleValue(width, sizeLimit, height);
      width = sizeLimit;
    }

    if (height > sizeLimit) {
      width = this.scaleValue(height, sizeLimit, width);
      height = sizeLimit;
    }

    return { width, height };
  }

  resizeCanvasToDimensions(canvas, width, height) {
    let finalWidth, finalHeight;

    finalWidth = width;
    finalHeight = height;

    if (finalWidth > MAX_SIZE) {
      finalHeight = this.scaleValue(finalWidth, MAX_SIZE, finalHeight);
      finalWidth = MAX_SIZE;
    }

    if (finalHeight > MAX_SIZE) {
      finalWidth = this.scaleValue(finalHeight, MAX_SIZE, finalWidth);
      finalHeight = MAX_SIZE;
    }

    canvas.width = finalWidth;
    canvas.height = finalHeight;
  }

  resizeCanvasForImage(canvas, image: HTMLImageElement) {
    this.resizeCanvasToDimensions(canvas, image.width, image.height);
  }

  drawImage(canvas, image: HTMLImageElement) {
    const context = canvas.getContext('2d');

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.imageSmoothingEnabled = false;
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
  }

  /**
   * Creates a rotated version of the specified image, while also restricting
   * its size to `sizeLimit` if available, or `MAX_SIZE` if not.
   * 
   * @param {*} canvas 
   * @param {*} image 
   * @param {*} degrees 
   * @param {*} offsetFactorX 
   * @param {*} offsetFactorY 
   * @param {*} sizeLimit 
   */
  rotateImage(
    canvas: HTMLCanvasElement,
    image: HTMLImageElement,
    degrees?: number,
    offsetFactorX?: number,
    offsetFactorY?: number,
    sizeLimit?: number
  ): Promise<Blob> {
    const scaledRect = this.restrictSize(image, sizeLimit);
    canvas.width = scaledRect.height;
    canvas.height = scaledRect.width;

    const context = canvas.getContext('2d');
    context.rotate(degToRad(degrees));
    context.drawImage(
      image,
      scaledRect.width * offsetFactorX,
      scaledRect.height * offsetFactorY,
      scaledRect.width,
      scaledRect.height
    );

    const deferred = new Promise<Blob>(
      (resolve) => {
        canvas.toBlob((blob) => {
          blob['name'] = 'image.png';
          resolve(blob);
        });
      }
    );

    return deferred;
  }

  rotateImageLeft(canvas: HTMLCanvasElement, image: HTMLImageElement) {
    const thumbnail = this.rotateImage(
      canvas,
      image,
      -90,
      // To make the rotated image visible as expected, we need a (-w, 0) offset
      -1,
      0,
      128 // Limit thumbnails to 128px square maximum
    );

    const rotatedImage =  this.rotateImage(
      canvas,
      image,
      -90,
      // Rotation offset factors
      -1,
      0
    );

    return Promise.all([thumbnail, rotatedImage]);
  }

  rotateImageRight(canvas: HTMLCanvasElement, image: HTMLImageElement) {
    const thumbnail = this.rotateImage(
      canvas,
      image,
      90,
      // A 90-degree clockwise rotation requires a (0, h) offset
      0,
      -1,
      128 // Limit thumbnails to 128px square maximum
    );

    const rotatedImage = this.rotateImage(
      canvas,
      image,
      90,
      // A 90-degree clockwise rotation requires a (0, h) offset
      0,
      -1
    );

    return Promise.all([thumbnail, rotatedImage]);
  }

  restrictImageSize(canvas: HTMLCanvasElement, image: HTMLImageElement) {
    const scaledRect = this.restrictSize(image);
    canvas.width = scaledRect.width;
    canvas.height = scaledRect.height;

    const context = canvas.getContext('2d');
    context.drawImage(
      image,
      0,
      0,
      scaledRect.width,
      scaledRect.height
    );

    const deferred = new Promise<Blob>(
      (resolve) => {
        canvas.toBlob((blob) => {
          blob['name'] = 'image.png';
          resolve(blob);
        });
      }
    );

    return deferred;
  }

  createThumbnail(canvas: HTMLCanvasElement, image: HTMLImageElement) {
    const scaledRect = this.restrictSize(image, 128);
    canvas.width = scaledRect.width;
    canvas.height = scaledRect.height;

    const context = canvas.getContext('2d');
    context.drawImage(
      image,
      0,
      0,
      scaledRect.width,
      scaledRect.height
    );

    const deferred = new Promise<Blob>(
      (resolve) => {
        canvas.toBlob((blob) => {
          blob['name'] = 'thumbnail.png';
          resolve(blob);
        });
      }
    );

    return deferred;
  }

  limitSize(imgUrl: string) {
    const canvas = this.getCanvas();
    const img = new Image;

    const deferred = new Promise<Blob>(
      (resolve) => {
        img.onload = () => {
          this.resizeCanvasForImage(canvas, img);
          this.drawImage(canvas, img);
    
          canvas.toBlob((blob: Blob) => {
            blob['name'] = 'image.png';
            resolve(blob);
          })
        }
      }
    )

    img.src = imgUrl;
    return deferred;
  }
  

}